// ------------------------------------------------------------------------------
// name: useSafeQuery
// author: mudas
// created: 2025/8/19
// ------------------------------------------------------------------------------

import { useComputed } from 'common/hooks';
import { hashQueryKeyByOptions } from './utils';

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { QueryKey } from '@tanstack/query-core';

import { DebounceQueryOptions, useDebounceQuery } from './plugins/useDebounceQuery';
import { ThrottleQueryOptions, useThrottleQuery } from './plugins/useThrottleQuery';
import { FetchingDelayOptions, useFetchingDelay } from './plugins/useFetchingDelay';

/**
 * 支持 isFetching 延迟生效和防抖查询的 useQuery
 * @param options
 */
export const useSafeQuery = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> &
    DebounceQueryOptions &
    ThrottleQueryOptions &
    FetchingDelayOptions,
) => {
  const {
    queryKey,
    queryHash,
    queryKeyHashFn,

    fetchingDelay,

    debounceWait,
    debounceLeading,
    debounceTrailing,
    debounceMaxWait,

    throttleWait,
    throttleLeading,
    throttleTrailing,

    ...queryOptions
  } = options;

  const queryKeyHash = useComputed<string>(() => {
    return hashQueryKeyByOptions({ queryKey, queryHash, queryKeyHashFn });
  }, [queryKey, queryHash, queryKeyHashFn]);

  // --------------------------------------------------------------------------
  //
  // 针对 options 查询参数的修改插件
  //
  // --------------------------------------------------------------------------

  const [debounceQueryOptions] = useDebounceQuery<TQueryFnData, TError, TData, TQueryKey>({
    queryHash: queryKeyHash,
    enabled: queryOptions.enabled,

    debounceWait,
    debounceLeading,
    debounceTrailing,
    debounceMaxWait,
  });

  const [throttleQueryOptions] = useThrottleQuery<TQueryFnData, TError, TData, TQueryKey>({
    queryHash: queryKeyHash,
    enabled: queryOptions.enabled,

    throttleWait,
    throttleLeading,
    throttleTrailing,
  });

  // --------------------------------------------------------------------------
  //
  // 针对 response 查询结果的修改插件
  //
  // --------------------------------------------------------------------------

  const query = useQuery<TQueryFnData, TError, TData, TQueryKey>({
    queryKey, // 虽然不影响查询的触发，但在 invalidateQueries 等操作定位为目标时需要
    queryHash: queryKeyHash,
    enabled: queryOptions.enabled,

    ...queryOptions,
    ...debounceQueryOptions,
    ...throttleQueryOptions,
  });

  const [isFetching] = useFetchingDelay({
    fetchStatus: query.fetchStatus,
    fetchingDelay,
  });

  return {
    ...query,
    isFetching,
  };
};
